import { useCallback, useEffect, useReducer, useRef } from 'react';
import type { ReportData } from '@/types/report';
import { cloneReportData } from '../utils/cloneReportData';

const MAX_HISTORY_SIZE = 50;

type HistoryState = {
    past: ReportData[];
    future: ReportData[];
};

type HistoryAction =
    | { type: 'PUSH'; snapshot: ReportData }
    | { type: 'UNDO'; current: ReportData }
    | { type: 'REDO'; current: ReportData }
    | { type: 'RESET' };

const initialState: HistoryState = {
    past: [],
    future: [],
};

const trimPast = (past: ReportData[]) => {
    if (past.length <= MAX_HISTORY_SIZE) return past;
    return past.slice(past.length - MAX_HISTORY_SIZE);
};

const historyReducer = (state: HistoryState, action: HistoryAction) => {
    switch (action.type) {
        case 'PUSH': {
            const newPast = trimPast([...state.past, action.snapshot]);
            return { past: newPast, future: [] };
        }
        case 'UNDO': {
            if (state.past.length === 0) return state;
            const newPast = state.past.slice(0, -1);
            const newFuture = [action.current, ...state.future];
            return { past: newPast, future: newFuture };
        }
        case 'REDO': {
            if (state.future.length === 0) return state;
            const [, ...rest] = state.future;
            const newPast = trimPast([...state.past, action.current]);
            return { past: newPast, future: rest };
        }
        case 'RESET': {
            return initialState;
        }
        default:
            return state;
    }
};

export function useReportHistory() {
    const [state, dispatch] = useReducer(historyReducer, initialState);
    const stateRef = useRef(state);

    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    const push = useCallback((currentState: ReportData, options?: { alreadyCloned?: boolean }) => {
        const snapshot = options?.alreadyCloned ? currentState : cloneReportData(currentState);
        dispatch({ type: 'PUSH', snapshot });
    }, []);

    const undo = useCallback((currentState: ReportData) => {
        const { past } = stateRef.current;
        if (past.length === 0) return null;

        const previous = past[past.length - 1];
        dispatch({ type: 'UNDO', current: cloneReportData(currentState) });
        return previous;
    }, []);

    const redo = useCallback((currentState: ReportData) => {
        const { future } = stateRef.current;
        if (future.length === 0) return null;

        const next = future[0];
        dispatch({ type: 'REDO', current: cloneReportData(currentState) });
        return next;
    }, []);

    return {
        push,
        undo,
        redo,
        canUndo: state.past.length > 0,
        canRedo: state.future.length > 0,
    };
}
